<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';
import { formatDate } from '@/utils/dateUtils';
import { useCheckMaxPassengersComposable } from '../useCheckMaxPassengersComposable';
import { useBookComposable } from '../useBookComposable';
import { useModalComposable } from '@/views/composable';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import TableColumn from 'primevue/column';
import RoomInfoBooking from '@/components/book/CruiseOptionTable/RoomInfoBooking.vue';
import HotelPaxName from '../HotelPaxName.vue';

const props = defineProps<{
  isManagement: boolean;
  priorityStatus: number | undefined;
  dataPreHotelPax: any;
  dataPreHotel: object;
  isShowAddPax?: boolean;
  isSubmit?: boolean;
}>();

const emits = defineEmits<{
  addPax: [value: any];
  submitCruiseQuote: [];
  'update:dataPreHotel': [value: any];
  'update:dataPreHotelPax': [value: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const currentLanguage = getCurrentLocale();
const data = ref();
const expandedRowsPre = ref<any>({});
const hotelEditingRows = ref([]);
const resultTotals: any = ref([]);
const isShowAddPaxPre = ref<boolean>(false);
const isShowDetailedOccupancy = ref(true);
const roomDataUpdatedHotel: any = ref([]);

const { checkMaxRoom } = useCheckMaxPassengersComposable();
const { calculateRoomTotals } = useBookComposable();
const { handleOpenErrorMessage } = useModalComposable();

const dataPreHotel = computed({
  get() {
    return props.dataPreHotel;
  },
  set(value) {
    emits('update:dataPreHotel', value);
  }
});

const dataPreHotelPax = computed({
  get() {
    return props.dataPreHotelPax;
  },
  set(value) {
    emits('update:dataPreHotelPax', value);
  }
});

const onCloseHotelPax = (hotelData: any, cruiseIndex: number, indexRoom: number, hotelType: string, indexHotel: number) => {
  const roomData = cloneDeep(hotelData.data);
  roomData?.forEach((occItem: any) => {
    dataPreHotelPax.value[cruiseIndex][hotelType][0].hotels[indexHotel].rooms[indexRoom].roomInfo[`${occItem.field}RoomInfos`] = occItem.passengers.map((passenger: any) => {
      return {
        chdPassengers: passenger.children || [],
        infPassengers: passenger.infants || [],
        adtPassengers: passenger.people,
        email: passenger.email,
        roomRequests: passenger.roomRequests
      };
    });
  });
  emits('submitCruiseQuote');
};

const onRowEditSaveHotel = async ({ newData, index }: any, rooms: any, data: any, indexHotel: number) => {
  isShowDetailedOccupancy.value = false;
  const dataCopyHotel = { ...cloneDeep(data) };
  dataCopyHotel.hotels[indexHotel].rooms[index] = { ...newData };
  const listRoom: any = ref([]);
  dataCopyHotel.hotels.forEach((item: any) => {
    item.rooms.forEach((room: any) => {
      listRoom.value.push(room.roomInfo);
    });
  });
  const overAdultNumber = checkMaxRoom(listRoom.value, data.adt, data.numOfRooms);
  if (overAdultNumber) {
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_room'));
    const oldData = cloneDeep(rooms[index].roomInfo);
    rooms[index].roomInfo = oldData;
  } else {
    rooms[index].roomInfo = cloneDeep(newData.roomInfo);
    await emits('submitCruiseQuote');
  }
  isShowDetailedOccupancy.value = true;
};

const updateResult = (data: any) => {
  const roomTotals = calculateRoomTotals(data.event);
  const newEntryData = {
    indexOption: data.indexOption,
    indexHotel: data.indexHotel,
    indexRoom: data.indexRoom,
    ...roomTotals
  };

  const existingIndex = resultTotals.value.findIndex((item: any) => item.indexOption === newEntryData.indexOption && item.indexHotel === newEntryData.indexHotel && item.indexRoom === newEntryData.indexRoom);

  if (existingIndex > -1) {
    resultTotals.value[existingIndex] = newEntryData;
  } else {
    resultTotals.value.push(newEntryData);
  }
};

const addPaxPreHotel = (dataOption: any, index: number, indexHotel: number) => {
  emits('addPax', {
    hotelType: 'preHotel',
    dataOption: dataOption,
    component: HotelPaxName,
    retDate: props.dataPreHotel.data.preHotel[0].retDate,
    cruiseIndex: props.dataPreHotel.index,
    index: index,
    callback: onCloseHotelPax,
    indexHotel: indexHotel
  });
};

const handleSaveRoomDataCloneHotel = (data: any) => {
  const newEntry = {
    indexOption: data.indexOption,
    indexHotel: data.indexHotel,
    indexRoom: data.indexRoom,
    ...data.event
  };
  const existingIndex = roomDataUpdatedHotel.value.findIndex((item: any) => item.indexOption === newEntry.indexOption && item.indexHotel === newEntry.indexHotel && item.indexRoom === newEntry.indexRoom);
  roomDataUpdatedHotel.value[existingIndex] = newEntry;
};

const updateBookingRequestForm = (event: any, room: any, indexOption: number, indexHotel: number, indexRoom: number) => {
  updateResult({ event, indexOption, indexHotel, indexRoom });
  handleSaveRoomDataCloneHotel({ event, indexOption, indexHotel, indexRoom });
};

const onRowEditSaveHotelPre = (eventData: any) => {
  const hotelTemp = data.value[eventData.index];
  let isChange = false;
  if (hotelTemp.adt !== eventData.newData.adt || hotelTemp.numOfRooms !== eventData.newData.numOfRooms) {
    isChange = true;
  }
  if (isChange) {
    data.value[eventData.index].hotels.forEach((hotel: any) => {
      hotel.rooms.forEach((room: any) => {
        room.roomInfo.singleNo = 0;
        room.roomInfo.doubleNo = 0;
        room.roomInfo.tripleNo = 0;
        room.roomInfo.quadNo = 0;
        room.roomInfo.fiveInRoomNo = 0;
        room.roomInfo.sixInRoomNo = 0;
        room.detailedOccupancy = null;
      });
    });
    isShowAddPaxPre.value = false;
    toast.add({ severity: 'info', summary: '', detail: t('notification.info_delete_room_entered'), life: 5000 });
  }
  data.value[eventData.index].adt = eventData.newData.adt;
  data.value[eventData.index].chd = eventData.newData.chd;
  data.value[eventData.index].numOfRooms = eventData.newData.numOfRooms;
  emits('submitCruiseQuote');
};

const handleFindRoomHotel = (indexOption: number, indexHotel: number, indexRoom: number) => {
  return roomDataUpdatedHotel.value.find((itemData: any) => itemData.indexOption === indexOption && itemData.indexHotel === indexHotel && itemData.indexRoom === indexRoom);
};

const checkOccupancy = () => {
  const isValidPreHotel: boolean = true;
  data.value.forEach((option: any, indexOption: number) => {
    option.hotels.forEach((hotel: any, indexHotel: number) => {
      hotel.rooms.forEach((room: any, indexRoom: number) => {
        const occupancy = handleFindRoomHotel(indexOption, indexHotel, indexRoom);
        room.detailedOccupancy = occupancy || room.detailedOccupancy;
      });
    });
  });

  return isValidPreHotel;
};

onBeforeMount(() => {
  data.value = dataPreHotel.value.data.preHotel;
  data.value?.forEach((hotelOption: any, indexOption: number) => {
    hotelOption.key = `item${indexOption}`;
    hotelOption.hotels.forEach((hotel: any, indexHotel: number) => {
      hotel.rooms.forEach((room: any, indexRoom: number) => {
        roomDataUpdatedHotel.value.push({
          indexOption,
          indexHotel,
          indexRoom
        });
      });
    });
  });
});

onMounted(() => {
  data.value?.forEach((element: any, itemIndex: number) => {
    expandedRowsPre.value = { ...expandedRowsPre.value, [`item${itemIndex}`]: true };
  });
});

watch(
  () => props.isSubmit,
  () => {
    checkOccupancy();
  }
);
</script>
<template>
  <div>
    <div v-if="data.length">
      <DataTable :value="data" v-model:expandedRows="expandedRowsPre" dataKey="key" editMode="row" v-model:editingRows="hotelEditingRows" @row-edit-save="onRowEditSaveHotelPre($event)">
        <TableColumn :expander="true" class="w-3rem" />
        <TableColumn field="option" :header="`${$t('book_form.option')} ${$t('book_form.pre_hotel')}`">
          <template #body="slotProps"> {{ $t('book_form.option') }} {{ slotProps.index + 1 }} </template>
        </TableColumn>
        <TableColumn field="dest" :header="$t('book_form.destination')"> </TableColumn>
        <TableColumn field="depDate" :header="$t('book_form.departure')">
          <template #body="{ data }">
            {{ formatDate(data.depDate, 'dd-LLL-yyyy', true, currentLanguage) }}
          </template>
        </TableColumn>
        <TableColumn field="adt" :header="$t('book_form.adults')">
          <template #editor="{ data, field }">
            <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
          </template>
        </TableColumn>
        <TableColumn field="chd" :header="$t('book_form.children')">
          <template #editor="{ data, field }">
            <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
          </template>
        </TableColumn>
        <TableColumn field="numOfRooms" :header="$t('book_form.number_of_rooms')">
          <template #editor="{ data, field }">
            <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
          </template>
        </TableColumn>
        <TableColumn v-if="isManagement" :header="$t('common.edit')" :rowEditor="true" class="py-3"></TableColumn>

        <template #expansion="{ data: itemData, index: indexOption }">
          <div>
            <div v-for="(hotelData, indexHotel) in itemData.hotels" :key="'hotel' + indexHotel" :id="'hotel-table-' + indexHotel">
              <div>
                <RoomInfoBooking
                  :isManagement="isManagement"
                  :priorityStatus="priorityStatus"
                  v-model:isShowAddPaxHotel="isShowAddPaxPre"
                  :hotelData="hotelData"
                  :itemData="itemData"
                  :indexOption="indexOption"
                  :resultTotals="resultTotals"
                  :indexHotel="indexHotel"
                  :onCloseHotelPax="onCloseHotelPax"
                  :isShowDetailedOccupancy="isShowDetailedOccupancy"
                  @addPax="(data, index) => addPaxPreHotel(data, index, indexHotel)"
                  @onRowEditSaveHotel="(event, rooms, dataCommon, indexHotel) => onRowEditSaveHotel(event, rooms, dataCommon, indexHotel)"
                  @updateBookingRequestForm="(event, room, indexOption, indexHotel, indexRoom) => updateBookingRequestForm(event, room, indexOption, indexHotel, indexRoom)"
                />
              </div>
            </div>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep(.text-center .p-column-header-content) {
  justify-content: center;
}
.pre-line {
  white-space: pre-line;
}
</style>
